/* eslint-disable react/no-unknown-property */
import type { ThreeEvent } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'

import SceneContext from '../context'
import TargetPuck from './target-puck'
import type { TargetPuckRef } from './target-puck'

interface PhysicsHitBoxInterface {
  node: THREE.Mesh
  visible?: boolean
  getClickTargetPosition?: (arg: Array<number>) => void
}

function PhysicsHitBox({
  node,
  visible = false,
  getClickTargetPosition,
}: PhysicsHitBoxInterface) {
  const hitBoxRef = React.useRef<THREE.Mesh>(null)
  const targetRef = React.useRef<TargetPuckRef>(null)
  const pointerDownPos = React.useRef<{ x: number; y: number } | null>(null)

  const { disablePuck } = React.useContext(SceneContext)

  const isHitBoxFloor = (name: string) => /floor/i.test(name)
  const isHitBoxStairs = (name: string) => /stairs/i.test(name)

  const isDrag = (event: ThreeEvent<MouseEvent>) => {
    if (!pointerDownPos.current) return false
    const dx = event.nativeEvent.clientX - pointerDownPos.current.x
    const dy = event.nativeEvent.clientY - pointerDownPos.current.y
    return Math.sqrt(dx * dx + dy * dy) > 5
  }

  return (
    <>
      {!disablePuck &&
        (isHitBoxFloor(node.name) || isHitBoxStairs(node.name)) && (
          <TargetPuck
            targetSurface={hitBoxRef}
            ref={targetRef}
            ring={[0.25, 0.3, 50]}
          />
        )}
      <mesh
        ref={hitBoxRef}
        visible={visible}
        key={`physics-hitbox-mesh-${node.name}`}
        name={`physics-hitbox-mesh-${node.name}`}
        position={node.position}
        geometry={node.geometry}
        onPointerDown={(event) => {
          pointerDownPos.current = {
            x: event.nativeEvent.clientX,
            y: event.nativeEvent.clientY,
          }
        }}
        onPointerUp={(event) => {
          event.stopPropagation()
          if (event.button > 0) {
            return
          }
          if (isDrag(event)) return
          if (
            (isHitBoxStairs(node.name) || isHitBoxFloor(node.name)) &&
            getClickTargetPosition
          ) {
            const targetPosition = targetRef.current?.getPuckPosition()
            if (targetPosition) {
              getClickTargetPosition([
                targetPosition.x,
                targetPosition.y,
                targetPosition.z,
              ])
            }
          }
        }}
      >
        <meshBasicMaterial
          color="red"
          visible={visible}
          transparent
          opacity={0.3}
        />
      </mesh>
    </>
  )
}

export default PhysicsHitBox
